import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Info, Trash2, Edit, Save, X, AlertTriangle, ArrowLeft, Upload, Send } from 'lucide-react';
import { formatPrice } from '../lib/currency';
import { useTranslation } from 'react-i18next';
import { useCartContext } from '../context/CartContext';

export interface Product {
  id: string;
  name: { [key: string]: string };
  description: { [key: string]: string };
  price: number;
  currency: string;
  category: string;
  image: string;
  images?: string[];
  manual?: string;
  videoTutorial?: string;
  inStock: boolean;
  featured: boolean;
  discount?: number;
  isOnSale?: boolean;
  isSoldOut?: boolean;
  isRecommended?: boolean;
  isRental?: boolean;
  rentalPriceMonthly?: number;
  rentalInsuranceMonthly?: number;
  rentalHourlyRate?: number;
  vatIncluded?: boolean;
  order?: number;
  categories?: string[];
}

export interface Category {
  id: string;
  name: string;
  color: string;
}

export interface ProductCardProps {
  product: Product;
  index: number;
  isAdmin: boolean;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, dropIndex: number) => void;
  language: string;
  categories: Category[];
  onShowDetails: (product: Product) => void;
  sectionColor?: string;
  onRentalRequest?: (product: Product) => void;
  deleteConfirmText?: string;
}

export default function ProductCard({ 
  product, 
  index, 
  isAdmin, 
  onEdit, 
  onDelete, 
  onDragStart, 
  onDragOver, 
  onDrop, 
  language, 
  categories, 
  onShowDetails,
  sectionColor = 'cyan',
  onRentalRequest,
  deleteConfirmText
}: ProductCardProps) {
  const { t, i18n } = useTranslation();
  const { addToCart } = useCartContext();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(product);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const productImages = product.images && product.images.length > 0 ? product.images : [product.image];

  const handleSave = () => {
    onEdit(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(product);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete(product.id);
    setShowDeleteConfirm(false);
  };

  const originalPrice = product.price;
  const discountedPrice = product.discount ? originalPrice * (1 - product.discount / 100) : originalPrice;

  // Define color classes based on section
  const getColorClasses = () => {
    switch (sectionColor) {
      case 'pink':
        return {
          border: 'border-pink-500/20 hover:border-pink-400/40',
          price: 'text-pink-400',
          button: 'from-pink-500 to-pink-600 hover:from-pink-400 hover:to-pink-500'
        };
      case 'blue':
        return {
          border: 'border-blue-500/20 hover:border-blue-400/40',
          price: 'text-blue-400',
          button: 'from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500'
        };
      case 'green':
        return {
          border: 'border-green-500/20 hover:border-green-400/40',
          price: 'text-green-400',
          button: 'from-green-500 to-green-600 hover:from-green-400 hover:to-green-500'
        };
      case 'red':
        return {
          border: 'border-red-500/20 hover:border-red-400/40',
          price: 'text-red-400',
          button: 'from-[#EE1C23] to-red-600 hover:from-red-600 hover:to-[#EE1C23]'
        };
      default:
        return {
          border: 'border-cyan-500/20 hover:border-cyan-400/40',
          price: 'text-cyan-400',
          button: 'from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500'
        };
    }
  };

  const colorClasses = getColorClasses();

  const handleAddToCart = () => {
    if (product.isSoldOut) {
      return;
    }
    setShowQuantityModal(true);
  };

  const confirmAddToCart = async () => {
    const productId = product.id || (product as any).id;
    const currentLang = i18n.language === 'cz' ? 'cs' : i18n.language;
    const productName = product.name[currentLang] || product.name.cs || product.name['cs'] || 'Produkt';

    const cartCategories = JSON.parse(localStorage.getItem('cart_categories') || '{}');
    cartCategories[productId] = product.category;
    localStorage.setItem('cart_categories', JSON.stringify(cartCategories));

    await addToCart(productId, selectedQuantity);

    // Create floating cart animation
    const productCard = document.querySelector(`[data-product-id="${product.id}"]`);
    if (productCard) {
      const rect = productCard.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      // Create floating product image
      const floatingElement = document.createElement('div');
      floatingElement.innerHTML = `
        <div style="
          width: 60px; 
          height: 60px; 
          background-image: url('${product.image}'); 
          background-size: cover; 
          background-position: center;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          border: 3px solid #22c55e;
        "></div>
      `;
      
      floatingElement.style.cssText = `
        position: fixed;
        left: ${centerX - 30}px;
        top: ${centerY - 30}px;
        z-index: 9999;
        pointer-events: none;
        transition: all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        transform: scale(1);
        opacity: 1;
      `;
      
      document.body.appendChild(floatingElement);
      
      // Animate to cart icon
      setTimeout(() => {
        const cartIcon = document.querySelector('nav a[href="/cart"] svg');
        if (cartIcon) {
          const cartRect = cartIcon.getBoundingClientRect();
          floatingElement.style.left = (cartRect.left + cartRect.width / 2 - 15) + 'px';
          floatingElement.style.top = (cartRect.top + cartRect.height / 2 - 15) + 'px';
          floatingElement.style.transform = 'scale(0.3)';
          floatingElement.style.opacity = '0';
        }
      }, 100);
      
      // Remove element after animation
      setTimeout(() => {
        if (document.body.contains(floatingElement)) {
          document.body.removeChild(floatingElement);
        }
      }, 1300);
      
      // Create success notification
      const notification = document.createElement('div');
      notification.innerHTML = `
        <div style="
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(34, 197, 94, 0.3);
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        ">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          ${t('shop.addedToCart')}: ${selectedQuantity}x ${productName}
        </div>
      `;
      
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        pointer-events: none;
        transform: translateX(100%);
        transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      `;
      
      document.body.appendChild(notification);
      
      // Slide in notification
      setTimeout(() => {
        notification.style.transform = 'translateX(0)';
      }, 200);
      
      // Slide out notification
      setTimeout(() => {
        notification.style.transform = 'translateX(100%)';
      }, 2500);
      
      // Remove notification
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
    }

    setShowQuantityModal(false);
    setSelectedQuantity(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      data-product-id={product.id}
      className={`bg-black/40 backdrop-blur-md border ${colorClasses.border} rounded-2xl overflow-hidden transition-all hover:scale-105 relative h-[240px] flex flex-col ${
        isAdmin ? 'cursor-move' : ''
      }`}
      draggable={isAdmin && !isEditing}
      onDragStart={(e) => onDragStart(e as unknown as React.DragEvent, index)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e as unknown as React.DragEvent, index)}
    >
      {/* Admin Controls */}
      {isAdmin && (
        <div className="absolute top-2 right-2 z-10 flex space-x-1">
          <button
            onClick={() => onEdit(product)}
            className="w-8 h-8 bg-gray-600 hover:bg-gray-500 text-white rounded-full flex items-center justify-center transition-colors"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-8 h-8 bg-red-600 hover:bg-red-500 text-white rounded-full flex items-center justify-center transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Status Badges */}
      <div className="absolute top-2 left-2 z-10 flex flex-col space-y-1">
        {product.inStock && !product.isSoldOut && (
          <span className="bg-green-600 text-white px-1 py-0.5 rounded text-[10px] font-bold flex items-center">
            <AlertTriangle className="w-2 h-2 mr-0.5" />
            SKLADEM
          </span>
        )}
        {product.isSoldOut && (
          <span className="bg-gray-600 text-white px-1 py-0.5 rounded text-[10px] font-bold flex items-center">
            <AlertTriangle className="w-2 h-2 mr-0.5" />
            VYPRODÁNO
          </span>
        )}
        {product.isRental && (
          <span className="bg-amber-600 text-white px-1 py-0.5 rounded text-[10px] font-bold">
            PUJČOVNA
          </span>
        )}
      </div>

      <div className="aspect-video bg-gray-800 overflow-hidden" style={{height: '96px'}}>
        <div className="relative w-full h-full">
          <img 
            src={productImages[currentImageIndex]} 
            alt={product.name[language] || product.name.cs}
            className="w-full h-full object-cover"
          />
          {productImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => prev === 0 ? productImages.length - 1 : prev - 1);
                }}
                className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                ‹
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => prev === productImages.length - 1 ? 0 : prev + 1);
                }}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                ›
              </button>
              <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-1">
                {productImages.map((_, index) => (
                  <div
                    key={index}
                    className={`w-1.5 h-1.5 rounded-full ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      
      <div className="p-2.5 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-1">
          <h3 className="text-base font-bold text-white">
            {product.name[language] || product.name.cs}
          </h3>
          
          <div className="text-right">
            {product.discount && product.discount > 0 ? (
              <div className="flex flex-col items-end">
                <div className="text-[10px] text-gray-400 line-through whitespace-nowrap">
                  {formatPrice(originalPrice, i18n.language)}
                </div>
                <div className={`text-base font-bold ${colorClasses.price} whitespace-nowrap`}>
                  {formatPrice(discountedPrice, i18n.language)}
                </div>
                <div className="text-[10px] text-red-400">-{product.discount}%</div>
                {product.category !== 'unlimited' && (
                  <div className="text-[9px] text-gray-500 mt-0.5">
                    {product.vatIncluded ? t('shop.vatIncluded') : t('shop.vatExcluded')}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-end">
                <span className={`text-base font-bold ${colorClasses.price} whitespace-nowrap`}>
                  {formatPrice(product.price, i18n.language)}
                </span>
                {product.category !== 'unlimited' && (
                  <div className="text-[9px] text-gray-500 mt-0.5">
                    {product.vatIncluded ? t('shop.vatIncluded') : t('shop.vatExcluded')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Categories */}
        <div className="flex flex-wrap gap-1 mb-2">
          {product.categories && product.categories.length > 0 && (
            <>
              {product.categories.map((categoryId) => {
                const category = categories.find(c => c.id === categoryId);
                return category ? (
                  <span key={categoryId} className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white`} style={{backgroundColor: category.color}}>
                    {category.name}
                  </span>
                ) : null;
              })}
            </>
          )}
        </div>

        <div className="space-y-1.5 mt-auto">
          <button
            onClick={() => onShowDetails(product)}
            className="w-full flex items-center justify-center space-x-1 bg-gray-700 text-white py-1.5 rounded-lg hover:bg-gray-600 transition-colors text-sm"
          >
            <Info className="w-4 h-4" />
            <span className="text-sm">{t('shop.moreAboutProduct')}</span>
          </button>
          {product.isRental && onRentalRequest ? (
            <button
              onClick={() => onRentalRequest(product)}
              className={`w-full flex items-center justify-center space-x-1 bg-gradient-to-r ${colorClasses.button} text-white py-1.5 rounded-lg transition-all text-sm`}
            >
              <Send className="w-4 h-4" />
              <span className="text-sm">{t('shop.interestedInRental')}</span>
            </button>
          ) : (
            <button
              onClick={handleAddToCart}
              className={`w-full flex items-center justify-center space-x-1 ${
                product.isSoldOut
                  ? 'bg-gray-600 cursor-not-allowed'
                  : `bg-gradient-to-r ${colorClasses.button}`
              } text-white py-1.5 rounded-lg transition-all text-sm`}
              disabled={product.isSoldOut}
            >
              <ShoppingCart className="w-4 h-4" />
              <span className="text-sm">{product.isSoldOut ? t('shop.soldOut') : t('shop.addToCart')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Flame Badge for Recommended Products */}
      {product.isRecommended && (
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 z-30 pointer-events-none">
          <div className="relative">
            {/* Flame shape with decorative wings */}
            <svg width="180" height="60" viewBox="0 0 180 60" className="drop-shadow-[0_0_20px_rgba(249,115,22,0.8)]">
              {/* Left wing */}
              <path
                d="M 10 30 Q 5 25 8 20 Q 12 15 15 18 Q 18 22 20 30 Z"
                fill="url(#flameGradientLeft)"
                className="animate-pulse"
              />
              {/* Right wing */}
              <path
                d="M 170 30 Q 175 25 172 20 Q 168 15 165 18 Q 162 22 160 30 Z"
                fill="url(#flameGradientRight)"
                className="animate-pulse"
              />
              {/* Main flame body */}
              <path
                d="M 25 40 Q 30 20 40 15 Q 50 12 60 15 Q 70 12 80 10 Q 90 8 100 10 Q 110 12 120 15 Q 130 12 140 15 Q 150 20 155 40 Q 153 50 145 55 Q 135 58 125 56 Q 115 58 105 57 Q 95 58 85 57 Q 75 58 65 56 Q 55 58 45 55 Q 35 52 27 45 Z"
                fill="url(#flameGradient)"
                className="animate-pulse"
              />

              {/* Gradient definitions */}
              <defs>
                <linearGradient id="flameGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#fbbf24" stopOpacity="1" />
                  <stop offset="40%" stopColor="#f97316" stopOpacity="1" />
                  <stop offset="100%" stopColor="#dc2626" stopOpacity="0.9" />
                </linearGradient>
                <linearGradient id="flameGradientLeft" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#f97316" stopOpacity="0.6" />
                </linearGradient>
                <linearGradient id="flameGradientRight" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#f97316" stopOpacity="0.6" />
                </linearGradient>
              </defs>
            </svg>

            {/* Text overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-extrabold text-xs tracking-wider drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] animate-pulse" style={{ marginTop: '8px' }}>
                {t('shop.recommended')}
              </span>
            </div>

            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-radial from-orange-500/40 via-yellow-500/20 to-transparent blur-xl animate-pulse"></div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-20 rounded-2xl">
          <div className="bg-gray-800 p-2 rounded-lg text-center">
           <p className="text-white mb-1 text-[10px]">{deleteConfirmText || t('common.areYouSure')}</p>
            <div className="flex space-x-1 justify-center">
              <button
                onClick={handleDelete}
               className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors text-[10px]"
              >
                {t('common.yes')}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
               className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-500 transition-colors text-[10px]"
              >
                {t('common.no')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Selection Modal */}
      {showQuantityModal && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-20 rounded-2xl">
          <div className="bg-gray-800 p-5 rounded-lg text-center min-w-[240px]">
            <h3 className="text-white mb-4 text-base font-bold">
              {t('shop.selectQuantity')}
            </h3>
            <div className="flex items-center justify-center space-x-4 mb-5">
              <button
                onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                className="w-10 h-10 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center text-lg font-bold"
              >
                -
              </button>
              <span className="text-white font-semibold w-10 text-center text-lg">{selectedQuantity}</span>
              <button
                onClick={() => setSelectedQuantity(selectedQuantity + 1)}
                className="w-10 h-10 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center text-lg font-bold"
              >
                +
              </button>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowQuantityModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors text-sm"
              >
                {t('shop.cancel')}
              </button>
              <button
                onClick={confirmAddToCart}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded hover:bg-red-500 transition-colors text-sm"
              >
                {t('shop.add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}